import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // `?reason=no-scope` is set by the API interceptor when the server answers
  // NO_SCOPE_ASSIGNED: the account is valid and signed in, but has no venue or
  // company assigned, so nothing in the app is reachable. The default copy
  // ("restricted to administrators") is actively wrong for that case, and
  // "Login as Admin" invites a retry that cannot possibly help — the fix is an
  // administrator granting access upstream.
  const noScope = searchParams.get('reason') === 'no-scope';

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="error">
            {noScope ? 'No access assigned' : 'Access Denied'}
          </Typography>
          {noScope ? (
            <>
              <Typography variant="body1" paragraph>
                Your account is signed in, but it has no venue or company assigned,
                so there is nothing here you can open yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Ask an administrator to assign you a venue or company. Signing in
                again won't change this.
              </Typography>
            </>
          ) : (
            <Typography variant="body1" paragraph>
              You don't have permission to access this page. This area is restricted to administrators only.
            </Typography>
          )}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/')}
              sx={{ mr: noScope ? 0 : 2 }}
            >
              Go to Home
            </Button>
            {/* Hidden for the no-scope case: re-authenticating cannot grant
                access the account does not have, and offering it sends people
                round a loop instead of to an administrator. */}
            {!noScope && (
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
              >
                Login as Admin
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default UnauthorizedPage;
